import { Box, Divider } from "@mui/material";
import { groupBy } from "lodash";
import { observer } from "mobx-react-lite";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import SolidGradientColorPicker from "../../../../../../../components/common/SolidGradientColorPicker/SolidGradientColorPicker";
import { useAppStore } from "../../../../../../../hooks/useStores";
import useDesignEditorUtils, { FeatureType, Property } from "../../../../../../CustomUtils/UseDesignEditor";
import MainTabHeader from "../../../../../../components/common/MainTabHeader/MainTabHeader";
import { useActiveObject } from "../../../../../../hooks/useActiveObject";
import UseBrandConfigs from "../../../../../../hooks/useBrandConfigs.tsx";
import { useEditor } from "../../../../../../hooks/useEditor";
import { LayerType } from "../../../../../../types";
import SelectedBrandColors from "../Common/SelectedBrandColors/SelectedBrandColors.tsx";
import styles from "./ShapeFillColor.module.scss";

const ShapeFillColor = () => {
	const { t } = useTranslation("translation", { keyPrefix: "editor.tabs" });
	const { campaignStore } = useAppStore();
	const activeObject = useActiveObject() as any;
	const [color, setColor] = useState("");
	const { EditAllAds, getRgbAndOpacityFromRgba, updateColorOpacity } = useDesignEditorUtils();
	const editor = useEditor();
	const { modifyActiveObjectProperty } = UseBrandConfigs();
	const isFabricShape =
		activeObject?.type === LayerType.STATIC_RECT ||
		activeObject?.type === LayerType.STATIC_CIRCLE ||
		activeObject?.type === LayerType.STATIC_TRIANGLE;

	useEffect(() => {
		if (activeObject && activeObject.type === LayerType.STATIC_VECTOR) {
			const objects = activeObject._objects[0]._objects;
			const objectColors = groupBy(objects, "fill");
			campaignStore.vectorColors = {
				...campaignStore.vectorColors,
				colors: Object.keys(objectColors),
				colorMap: activeObject.colorMap,
			};

			Object.keys(campaignStore.vectorColors.colorMap).map((color, _index) => {
				campaignStore.prevColor = color;
			});

			setColor(
				updateColorOpacity(
					campaignStore?.vectorColors?.colorMap[campaignStore?.prevColor],
					activeObject.opacity,
				),
			);
		} else if (isFabricShape) {
			setColor(updateColorOpacity(activeObject.fill, activeObject.opacity));
		}
	}, [activeObject]);

	const changeBackgroundColor = (prev: string, next: string, brandColor: boolean = false) => {
		const objectRef = activeObject;
		const result = getRgbAndOpacityFromRgba(next);
		if (objectRef && objectRef.type === LayerType.STATIC_VECTOR) {
			objectRef?.updateLayerColor(prev, result?.rgb ?? next);
			campaignStore.vectorColors = {
				...campaignStore.vectorColors,
				colorMap: {
					...campaignStore.vectorColors.colorMap,
					[prev]: result?.rgb ?? next,
				},
			};
			editor?.objects.update(
				{
					opacity: brandColor ? undefined : result?.opacity ?? 1,
					brandId: undefined,
				},
				undefined,
				!campaignStore.isEditingAllAds,
			);
			EditAllAds(LayerType.STATIC_VECTOR, activeObject?.shapeType, FeatureType.COLOR, result?.rgb ?? next);
		} else if (isFabricShape) {
			editor?.objects.update(
				{
					fill: next,
					opacity: brandColor ? undefined : result?.opacity ?? 1,
					brandId: undefined,
				},
				undefined,
				!campaignStore.isEditingAllAds,
			);
			EditAllAds(activeObject.type, activeObject?.shapeType, FeatureType.COLOR, result?.rgb ?? next);
		}
	};

	return (
		<>
			<Box className={styles.container}>
				<MainTabHeader title={t("fillColor")} />
				<Box className={`${styles.tabs} ${styles.fullHeight}`}>
					<Box className={styles.ColorFillContent}>
						<Box className={styles.colorPickerContainer}>
							<SolidGradientColorPicker
								color={color}
								onChange={(color: string) => {
									setColor(color);
									changeBackgroundColor(campaignStore.prevColor, color);
									modifyActiveObjectProperty(activeObject, Property.COLOR_NUMBER, undefined);
								}}
								hideColorTypeBtns={true}
							/>
						</Box>
						<Divider className={styles.mainDivider} />
						<SelectedBrandColors
							activeObject={activeObject}
							updateColor={(color: string) => {
								setColor(color);
								changeBackgroundColor(campaignStore.prevColor, color, true);
							}}
							property={Property.COLOR_NUMBER}
						/>
					</Box>
				</Box>
			</Box>
		</>
	);
};

const ObservedComponent = observer(ShapeFillColor);
export default ObservedComponent;
